import { AddressElement, CardCvcElement, useElements } from '@stripe/react-stripe-js'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import controlStore from '../../../store/controlStore.ts'
import { FormLayout } from '../components/FormLayout.tsx'
import { Header } from '../components/Header.tsx'
import { Section } from '../components/Section.tsx'
import { AccountDetails } from './steps/AccountDetails.tsx'
import { ChoosePetAndContact } from './steps/ChoosePetAndContact.tsx'
import { Payment } from './steps/Payment.tsx'
import { SelectPlan } from './steps/SelectPlan.tsx'
import { ValidateTrackerId } from './steps/ValidateTrackerId.tsx'
import { appearance } from './stripeElementConfig.ts'

// type FormDataType = Record<string, any>;

export enum STEPS {
    'start',
    'iccid',
    'plan',
    'email',
    'payDetails',
    'pet',
    'contact'
}

const stepsComplete: Record<string, boolean> = {
    'start': false,
    'iccid': false,
    'plan': false,
    'email': false,
    'payDetails': false,
    'pet': false,
    'contact': false,
}

const emptyForm = {
    iccid: '',
    priceId: '',
    email: '',
    password: '',
    mobile: '',
    address: {
        line1: '',
        line2: '',
        city: '',
        country: '',
        postalCode: '',
        state: '',
        name: '',
    },
    useExistingPayment: false,
    cvcEntered: false,
    petId: '',
    contacts: [''],
    timeZone: '',
    smsOptIn: 'Opt-in'
}

export const RegisterUserForm: React.FC = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const createAccountData = location?.state?.data

    const logicalStep = createAccountData ? STEPS.pet : STEPS.iccid
    const [step, setStep] = React.useState(logicalStep)

    const [editIccid, setEditIccid] = React.useState(false)
    const [editPlan, setEditPlan] = React.useState(false)
    
    const isMobile = controlStore(s => s.isOnMobileDevice)

    const _setStep = (s: any) => {
        setStep(s)
    }

    const elements = useElements()
    // @ts-ignore
    elements?.update({appearance})

    const {
        register,
        // handleSubmit,
        control,
        getValues,
        formState: {errors},
        watch,
        // resetField,
        trigger,
        reset,
        setValue,
    } = useForm({
        defaultValues: createAccountData || emptyForm,
        mode: 'onChange',
        reValidateMode: 'onBlur',
    })

    const trackerId = watch('iccid')
    const priceId = watch('priceId')

    const resetForm = () => {
        reset()
        elements?.getElement(AddressElement)?.clear()
        elements?.getElement(CardCvcElement)?.clear()
        setStep(STEPS.iccid)
    }
    
    const width = !isMobile ? 'w-[32rem]' : ''
    
    return ( // eslint-disable-next-line @typescript-eslint/no-misused-promises
        <div className={`width-full flex justify-center`}>
        <FormLayout top width={width} full trigger={trigger} header={<Header title={'Create your Gibi account'}/>}>
            <form className={`bg-primary/10 mx-8 overflow-y-auto rounded-xl max-h-[95vh]`}>
                {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
                <div className={'flex flex-col gap-2 p-8 rounded'}>

                    <Section disabled={step !== STEPS.iccid && !editIccid}
                             title={'Validate your Gibi Tracker ID'}
                             step={STEPS.iccid}
                             done={stepsComplete.iccid && !editIccid}
                             onTap={() => setEditIccid(true)}>

                        {!(step !== STEPS.iccid && !editIccid) ?
                            <ValidateTrackerId
                                trackerId={trackerId}
                                errors={errors}
                                register={register}
                                onComplete={(b) => {
                                    stepsComplete.iccid = b
                                    if (b) {
                                        if (editIccid) {
                                            setEditIccid(false)
                                        } else {
                                            _setStep(STEPS.plan)
                                        }
                                    }
                                }}
                                disabled={step !== STEPS.iccid}
                            /> : null}
                    </Section>

                    <Section disabled={step !== STEPS.plan && !editPlan}
                             title={'Select your Gibi subscription plan'}
                             step={STEPS.plan}
                             done={stepsComplete.plan && !editPlan}
                             onTap={() => {
                                 if (!editPlan) {
                                     setEditPlan(true)
                                 } else if (priceId) {
                                     setEditPlan(false)
                                 }
                             }}>

                        {!(step !== STEPS.plan && !editPlan) ?
                            <SelectPlan
                                watch={watch}
                                onComplete={(b) => {
                                    stepsComplete.plan = b
                                    if (b) {
                                        if (editPlan) {
                                            setEditPlan(false)
                                        } else {
                                            _setStep(STEPS.email)
                                        }
                                    }
                                }}
                                errors={errors}
                                control={control}
                                createAccount={true}
                            /> : null}
                    </Section>

                    <AccountDetails
                        disabled={step !== STEPS.email}
                        control={control}
                        setValue={setValue}
                        register={register}
                        STEPS={STEPS}
                        watch={watch}
                        onComplete={() => _setStep(STEPS.payDetails)}
                        errors={errors}
                        getValues={getValues}
                    />

                    <Payment
                        control={control}
                        createAccount={true}
                        disabled={step !== STEPS.payDetails}
                        errors={errors}
                        register={register}
                        watch={watch}
                        getValues={getValues}
                        onComplete={(r) => {
                            if (r.status && !r.error) {
                                _setStep(STEPS.pet)
                            }
                        }}
                        STEPS={STEPS}
                    />

                    <ChoosePetAndContact
                        control={control}
                        data={getValues()}
                        disabled={step !== STEPS.pet}
                        watch={watch}
                        setValue={setValue}
                        petCreated={createAccountData}
                        STEPS={STEPS}
                        createAccount={true}
                    />

                    {step === STEPS.pet || step === STEPS.contact ? <div className={'btn mt-4'}
                                                                         onClick={() => navigate(`/map`)}> Done </div> : null}
                    {step === STEPS.iccid || step === STEPS.plan || step === STEPS.email || step === STEPS.payDetails ?
                        <input className={'btn mt-4 text-primary-content'} type="reset" onClick={resetForm}/> : null}
                    <input
                        type={'button'}
                        className={'btn btn-ghost text-primary mt-4'}
                        onClick={() => navigate('/', {replace: true})}
                        value={'Cancel'}
                    />
                </div>
            </form>
        </FormLayout>
        </div>
    )
}


